import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { verifyAuth, refreshAccessToken, logout, setCredentials, setRedirectAfterLogin, clearRedirectAfterLogin, showSessionTimeout } from '../../slices/authSlice';
import LoadingScreen from '../shared/LoadingScreen';
import { setAuthErrorHandler } from '../../services/apiService';
import websocketService from '../../services/websocketService';

// Debounce flag to prevent concurrent verification attempts
let verificationInProgress = false;
let lastVerificationTime = 0;
const VERIFICATION_DEBOUNCE_MS = 1000;

const AuthRouterWrapper = ({ children }) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const { user, loading } = useSelector((state) => state.auth);
  const locationRef = useRef(location);
  const navigateRef = useRef(navigate);

  useEffect(() => {
    locationRef.current = location;
    navigateRef.current = navigate;
  }, [location, navigate]);

  useEffect(() => {
    // Helper function to get login URL
    // Bypass token is handled via httpOnly cookie by the server — no localStorage needed
    const getLoginUrl = () => {
      return '/login';
    };

    // Set the auth error handler for API service
    setAuthErrorHandler(() => {
      // Store current URL before redirecting (for redirect after re-login)
      const currentPath = locationRef.current.pathname + locationRef.current.search;
      setRedirectAfterLogin(currentPath);

      // Show session timeout modal - modal handles logout + navigate after countdown
      dispatch(showSessionTimeout());
    });

    // Listen for session expired events from axios
    const handleSessionExpired = (event) => {
      const { code, message } = event.detail || {};

      // Store current URL before redirecting (for redirect after re-login)
      const currentPath = locationRef.current.pathname + locationRef.current.search;
      setRedirectAfterLogin(currentPath);

      // TC-AUTH-008: Handle concurrent session differently from regular session timeout
      if (code === 'CONCURRENT_SESSION') {
        localStorage.removeItem('token');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('user');
        localStorage.removeItem('loginTimestamp');
        localStorage.removeItem('tokenExpiresAt');

        // Dispatch logout to clear Redux state
        dispatch(logout());

        // Navigate to login - the LoginPage will show the concurrent session message
        // from localStorage flags set by axios interceptor
        navigateRef.current(getLoginUrl(), { replace: true });
      } else {
        // Regular session timeout - show modal; modal handles logout + navigate after countdown
        dispatch(showSessionTimeout());
      }
    };

    // Handle concurrent session (separate event from apiService for CONCURRENT_SESSION /
    // REFRESH_TOKEN_MISMATCH / TOKEN_REUSE_DETECTED error codes)
    const handleConcurrentSession = () => {
      const currentPath = locationRef.current.pathname + locationRef.current.search;
      setRedirectAfterLogin(currentPath);
      localStorage.removeItem('token');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('user');
      localStorage.removeItem('loginTimestamp');
      localStorage.removeItem('tokenExpiresAt');
      dispatch(logout());
      navigateRef.current(getLoginUrl(), { replace: true });
    };

    window.addEventListener('auth:sessionExpired', handleSessionExpired);
    window.addEventListener('auth:concurrentSession', handleConcurrentSession);

    // Bridge WebSocket auth errors to Redux session timeout modal
    const handleWsAuthError = (data) => {
      const currentPath = locationRef.current.pathname + locationRef.current.search;
      setRedirectAfterLogin(currentPath);

      if (data?.code === 'CONCURRENT_SESSION' || data?.code === 'TOKEN_REUSE_DETECTED') {
        localStorage.setItem('concurrentSessionDetected', 'true');
        localStorage.setItem('concurrentSessionMessage', data?.message || 'Logged in from another device.');
        dispatch(logout());
        navigateRef.current(getLoginUrl(), { replace: true });
      } else {
        // Regular session timeout - show modal; modal handles logout + navigate after countdown
        dispatch(showSessionTimeout());
      }
    };

    websocketService.on('auth_error', handleWsAuthError);
    websocketService.on('session_expired', handleWsAuthError);

    // Skip auth check on login page to prevent auto-refresh during login attempts
    if (locationRef.current.pathname === '/login') {
      return () => {
        window.removeEventListener('auth:sessionExpired', handleSessionExpired);
        window.removeEventListener('auth:concurrentSession', handleConcurrentSession);
        websocketService.off('auth_error', handleWsAuthError);
        websocketService.off('session_expired', handleWsAuthError);
      };
    }

    const checkAuth = async () => {
      // Debounce rapid concurrent calls
      if (verificationInProgress) {
        return;
      }

      const now = Date.now();
      if (now - lastVerificationTime < VERIFICATION_DEBOUNCE_MS) {
        return;
      }

      verificationInProgress = true;
      lastVerificationTime = now;

      const storedUser = localStorage.getItem('user');
      const loginTimestamp = localStorage.getItem('loginTimestamp');

      if (storedUser) {
        // Trust httpOnly cookies for auth - just call verifyAuth if user exists in localStorage
        // Tokens are stored in httpOnly cookies by the backend, not in localStorage

        try {
          const parsedUser = JSON.parse(storedUser);
          const storedTokenExpiresAt = localStorage.getItem('tokenExpiresAt');
          dispatch(setCredentials({
            accessToken: null,
            refreshToken: null,
            user: parsedUser,
            loginTimestamp: loginTimestamp ? parseInt(loginTimestamp) : null,
            tokenExpiresAt: storedTokenExpiresAt ? parseInt(storedTokenExpiresAt) : null,
          }));

          // Try to verify the current token
          await dispatch(verifyAuth()).unwrap();

          // Verification succeeded, user is authenticated
          // Clear any stored redirect URL since user is now authenticated
          clearRedirectAfterLogin();
        } catch (error) {

          try {
            // Try silent refresh
            await dispatch(refreshAccessToken()).unwrap();
          } catch (refreshError) {
            // Store current URL before redirecting (for redirect after re-login)
            const currentPath = locationRef.current.pathname + locationRef.current.search;
            setRedirectAfterLogin(currentPath);

            // Dispatch logout to clear Redux state
            dispatch(logout());
            // Let AuthTimeoutManager handle showing the modal (checks localStorage flag)
            // Navigate to login page with bypass token preserved
            navigateRef.current(getLoginUrl(), { replace: true });
          }
        }
      } else {
        // No tokens, ensure loading is false so ProtectedRoute can redirect
        dispatch({ type: 'auth/setLoading', payload: false });
      }

      verificationInProgress = false;
    };

    checkAuth();

    // Cleanup function
    return () => {
      window.removeEventListener('auth:sessionExpired', handleSessionExpired);
      window.removeEventListener('auth:concurrentSession', handleConcurrentSession);
      websocketService.off('auth_error', handleWsAuthError);
      websocketService.off('session_expired', handleWsAuthError);
    };
    // Register listeners once: dispatch is stable; navigate/location are read via refs so this effect
    // does not re-run (and re-register handlers) on every route change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  // Show loading while checking authentication, but not on login page
  if (loading && location.pathname !== '/login') {
    return <LoadingScreen />;
  }

  // Render children (should be wrapped in ProtectedRoute)
  return children;
};

export default AuthRouterWrapper;
